import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Star, MapPin, Users, Check, Shield, MessageCircle, Flame, Sparkles, Wallet, CreditCard, MapPinned } from "lucide-react";
import { toast } from "sonner";
import FavoriteButton from "@/components/FavoriteButton";
import ChatDialog from "@/components/ChatDialog";

export default function PoolDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { t, user } = useApp();
  const [pool, setPool] = useState(null);
  const [date, setDate] = useState(() => {
    const d = searchParams.get("date");
    return d ? new Date(d) : null;
  });
  const [guests, setGuests] = useState(() => Math.max(1, parseInt(searchParams.get("guests") || "1", 10)));
  const [busy, setBusy] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [slot, setSlot] = useState(searchParams.get("slot") === "half" ? "half" : "full");
  const [slotInfo, setSlotInfo] = useState(null);
  const [payDialogOpen, setPayDialogOpen] = useState(false);

  useEffect(() => { api.get(`/pools/${id}`).then((r) => setPool(r.data)); }, [id]);
  useEffect(() => {
    if (!date) { setSlotInfo(null); return; }
    const dateStr = date.toISOString().slice(0, 10);
    api.get(`/pools/${id}/slots`, { params: { date: dateStr } }).then((r) => {
      setSlotInfo(r.data);
      if (!r.data.full.enabled && r.data.half.enabled) setSlot("half");
      else if (!r.data.half.enabled && r.data.full.enabled) setSlot("full");
    }).catch(() => setSlotInfo(null));
  }, [date, id]);

  // Auto-open payment dialog if user landed here after login redirect
  useEffect(() => {
    if (user && searchParams.get("resume") === "1" && date) {
      setPayDialogOpen(true);
    }
    // eslint-disable-next-line
  }, [user, pool]);

  if (!pool) return <div className="max-w-7xl mx-auto p-12">Loading...</div>;

  const photos = pool.photos?.length ? pool.photos.map(fileUrl) : [
    "https://images.unsplash.com/photo-1582533568805-78a15dcb01b5?w=1200",
    "https://images.unsplash.com/photo-1761138785581-194503520539?w=800",
    "https://images.unsplash.com/photo-1760926455212-d42b180ad602?w=800",
  ];

  const subtotal = pool.price_per_day * guests;
  const slotBasePct = slot === "half" ? (pool.half_day_price_pct ?? 0.6) : 1.0;
  const slotSubtotal = +(subtotal * slotBasePct).toFixed(2);
  const onPromo = pool.promo_active && pool.discount_pct > 0;
  const discounted = onPromo ? +(slotSubtotal * (1 - pool.discount_pct)).toFixed(2) : slotSubtotal;
  const total = discounted;
  const unavailable = (pool.unavailable_dates || []).map((d) => new Date(d));

  const openBookDialog = () => {
    if (!date) { toast.error(t("pool.chooseDate")); return; }
    if (!user) {
      // Preserva selezione nella query string per riportare qui dopo login
      const dateStr = date.toISOString().slice(0, 10);
      const back = `/pool/${pool.pool_id}?date=${dateStr}&guests=${guests}&slot=${slot}&resume=1`;
      nav(`/auth?redirect=${encodeURIComponent(back)}`);
      return;
    }
    setPayDialogOpen(true);
  };

  const confirmBooking = async (paymentMethod) => {
    setBusy(true);
    try {
      const dateStr = date.toISOString().slice(0, 10);
      const { data } = await api.post("/bookings/checkout", {
        pool_id: pool.pool_id, date: dateStr, guests, slot,
        origin_url: window.location.origin,
        payment_method: paymentMethod,
      });
      setPayDialogOpen(false);
      nav(`/payment/success?booking_id=${data.booking_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Prenotazione fallita");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      {/* Bento Gallery */}
      <div className="relative grid grid-cols-4 grid-rows-2 gap-3 h-[420px] mb-8 rounded-3xl overflow-hidden">
        <div className="col-span-2 row-span-2"><img src={photos[0]} alt="" className="w-full h-full object-cover" /></div>
        <div><img src={photos[1] || photos[0]} alt="" className="w-full h-full object-cover" /></div>
        <div><img src={photos[2] || photos[0]} alt="" className="w-full h-full object-cover" /></div>
        <div><img src={photos[3] || photos[1] || photos[0]} alt="" className="w-full h-full object-cover" /></div>
        <div><img src={photos[4] || photos[2] || photos[0]} alt="" className="w-full h-full object-cover" /></div>
        {user?.role === "guest" && <div className="absolute top-4 right-4"><FavoriteButton poolId={pool.pool_id} className="!w-11 !h-11" /></div>}
      </div>

      <div className="grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <div className="flex items-center gap-3 mb-3 flex-wrap">
            <div className="overline text-[#E27B58]">{pool.hotel_name}</div>
            {pool.review_count > 0 && (
              <div className="flex items-center gap-1 text-sm">
                <Star className="w-4 h-4 fill-[#E27B58] text-[#E27B58]" />
                <span className="font-semibold">{pool.rating_avg.toFixed(1)}</span>
                <span className="text-muted-foreground">· {pool.review_count} {t("pool.reviews")}</span>
              </div>
            )}
          </div>
          <h1 className="font-display text-4xl font-bold mb-2 tracking-tight">{pool.title}</h1>
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
            <MapPin className="w-4 h-4" /> {pool.address}, {pool.city}, {pool.country}
          </div>
          {pool.daily_offer && (
            <div className="mb-4 inline-flex items-center gap-2 bg-green-500 text-white px-4 py-2 rounded-lg text-sm font-bold shadow-md" data-testid="daily-offer-banner">
              <Sparkles className="w-4 h-4" /> {pool.daily_offer}
            </div>
          )}
          {onPromo && (
            <div className="mb-6 inline-flex items-center gap-2 bg-[#E27B58]/10 border border-[#E27B58]/30 text-[#C56A4A] px-4 py-2 rounded-full text-sm font-semibold" data-testid="promo-banner">
              <Flame className="w-4 h-4" /> {pool.promo_label || `−${Math.round(pool.discount_pct * 100)}%`} <span className="opacity-70">· {t("promo.badge")}</span>
            </div>
          )}
          {user?.role === "guest" && (
            <div className="mb-6">
              <Button variant="outline" onClick={() => setChatOpen(true)} className="rounded-full" data-testid="contact-host">
                <MessageCircle className="w-4 h-4 mr-2" /> {t("chat.contactHost")}
              </Button>
            </div>
          )}
          <div className="prose max-w-none mb-8">
            <div className="overline text-[#005C7A] mb-2">{t("pool.about")}</div>
            <p className="text-muted-foreground leading-relaxed whitespace-pre-line">{pool.description}</p>
          </div>
          <div className="mb-10">
            <div className="overline text-[#005C7A] mb-3">{t("pool.amenities")}</div>
            <div className="grid grid-cols-2 gap-3">
              {pool.amenities?.map((a) => (
                <div key={a} className="flex items-center gap-2 text-sm"><Check className="w-4 h-4 text-[#005C7A]" /> {t(`amenity.${a}`)}</div>
              ))}
            </div>
          </div>

          {/* Guest reviews */}
          <div className="border-t border-border pt-8 mb-10">
            <div className="flex items-center gap-2 mb-4">
              <Star className="w-5 h-5 fill-[#E27B58] text-[#E27B58]" />
              <h3 className="font-display text-2xl font-bold">{t("pool.guestReviews")}</h3>
            </div>
            {(!pool.reviews || pool.reviews.length === 0) ? (
              <p className="text-muted-foreground">{t("pool.noReviews")}</p>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {pool.reviews.map((r) => (
                  <div key={r.review_id} className="bg-white border border-border rounded-xl p-5">
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-semibold">{r.guest_name}</div>
                      <div className="flex gap-0.5">{Array.from({ length: 5 }).map((_, i) => <Star key={i} className={`w-3.5 h-3.5 ${i < r.rating ? "fill-[#E27B58] text-[#E27B58]" : "text-muted-foreground/30"}`} />)}</div>
                    </div>
                    <p className="text-sm text-muted-foreground">{r.comment}</p>
                    {r.photos?.length > 0 && (
                      <div className="grid grid-cols-3 gap-2 mt-3">
                        {r.photos.map((p) => (
                          <a key={p} href={fileUrl(p)} target="_blank" rel="noreferrer" className="aspect-square rounded-lg overflow-hidden bg-muted">
                            <img src={fileUrl(p)} alt="" className="w-full h-full object-cover hover:scale-105 transition" />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Host reviews of guests */}
          <div className="border-t border-border pt-8">
            <div className="flex items-center gap-2 mb-1">
              <Shield className="w-5 h-5 text-[#005C7A]" />
              <h3 className="font-display text-2xl font-bold">{t("pool.hostReviews")}</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-4">{t("pool.hostReviewsHelper")}</p>
            {(!pool.host_reviews || pool.host_reviews.length === 0) ? (
              <p className="text-muted-foreground">{t("pool.noHostReviews")}</p>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {pool.host_reviews.map((r) => (
                  <div key={r.host_review_id} className="bg-[#005C7A]/5 border border-[#005C7A]/20 rounded-xl p-5">
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-semibold flex items-center gap-1.5"><Shield className="w-3.5 h-3.5 text-[#005C7A]" /> {r.owner_name}</div>
                      <div className="flex gap-0.5">{Array.from({ length: 5 }).map((_, i) => <Star key={i} className={`w-3.5 h-3.5 ${i < r.rating ? "fill-[#E27B58] text-[#E27B58]" : "text-muted-foreground/30"}`} />)}</div>
                    </div>
                    <p className="text-sm text-muted-foreground">{r.comment}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <aside className="lg:sticky lg:top-24 h-fit">
          <div className="bg-white border border-border rounded-2xl p-6 shadow-sm">
            <div className="flex items-baseline gap-1 mb-1">
              {onPromo && <span className="text-lg line-through text-muted-foreground mr-1">€{pool.price_per_day}</span>}
              <span className="font-display text-3xl font-bold">€{onPromo ? +(pool.price_per_day * (1 - pool.discount_pct)).toFixed(2) : pool.price_per_day}</span>
              <span className="text-sm text-muted-foreground">/ {t("pool.perDay")}</span>
            </div>
            <div className="inline-flex items-center gap-1.5 bg-[#005C7A]/10 text-[#005C7A] text-[11px] font-semibold px-2.5 py-1 rounded-full mb-4" data-testid="pay-on-site-badge">
              <MapPinned className="w-3 h-3" /> Da pagare in loco
            </div>
            <div className="mb-4">
              <div className="text-xs font-semibold uppercase tracking-wider mb-2">{t("pool.chooseDate")}</div>
              <Calendar mode="single" selected={date} onSelect={setDate} disabled={(d) => d < new Date(new Date().setHours(0,0,0,0)) || unavailable.some((u) => u.toDateString() === d.toDateString())} className="rounded-md border border-border" data-testid="booking-calendar" />
            </div>
            <div className="mb-4">
              <div className="text-xs font-semibold uppercase tracking-wider mb-2">{t("pool.guests")}</div>
              <div className="flex items-center gap-3 border border-border rounded-lg px-3 py-2">
                <Users className="w-4 h-4 text-muted-foreground" />
                <input type="number" min="1" max={pool.max_guests} value={guests} onChange={(e) => setGuests(Math.max(1, Math.min(pool.max_guests, +e.target.value)))} className="flex-1 outline-none bg-transparent" data-testid="guests-input" />
                <span className="text-xs text-muted-foreground">max {pool.max_guests}</span>
              </div>
            </div>
            {date && slotInfo && (slotInfo.full.enabled || slotInfo.half.enabled) && (
              <div className="mb-4">
                <div className="text-xs font-semibold uppercase tracking-wider mb-2">{t("pool.slot")}</div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    disabled={!slotInfo.full.enabled}
                    onClick={() => setSlot("full")}
                    className={`p-3 rounded-lg border-2 text-left transition ${slot === "full" && slotInfo.full.enabled ? "border-[#005C7A] bg-[#005C7A]/5" : "border-border"} ${!slotInfo.full.enabled ? "opacity-40 cursor-not-allowed" : ""}`}
                    data-testid="slot-full-btn"
                  >
                    <div className="text-xs font-semibold text-[#005C7A]">{t("pool.slotFull")}</div>
                    <div className="text-sm font-bold mt-1">€{slotInfo.full.price}<span className="text-xs text-muted-foreground font-normal">/pers</span></div>
                  </button>
                  <button
                    disabled={!slotInfo.half.enabled}
                    onClick={() => setSlot("half")}
                    className={`p-3 rounded-lg border-2 text-left transition ${slot === "half" && slotInfo.half.enabled ? "border-[#E27B58] bg-[#E27B58]/5" : "border-border"} ${!slotInfo.half.enabled ? "opacity-40 cursor-not-allowed" : ""}`}
                    data-testid="slot-half-btn"
                  >
                    <div className="text-xs font-semibold text-[#E27B58]">{t("pool.slotHalf")}</div>
                    <div className="text-sm font-bold mt-1">€{slotInfo.half.price}<span className="text-xs text-muted-foreground font-normal">/pers</span></div>
                  </button>
                </div>
              </div>
            )}
            {date && slotInfo && !slotInfo.full.enabled && !slotInfo.half.enabled && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-testid="no-slots">{t("pool.noSlot")}</div>
            )}
            <div className="space-y-2 text-sm border-t border-border pt-4 mb-4">
              <div className="flex justify-between"><span>{t("pool.subtotal")} · {slot === "half" ? t("pool.slotHalf") : t("pool.slotFull")}</span><span>€{slotSubtotal.toFixed(2)}</span></div>
              {onPromo && <div className="flex justify-between text-[#E27B58] font-semibold"><span>Promo −{Math.round(pool.discount_pct * 100)}%</span><span>−€{(slotSubtotal - discounted).toFixed(2)}</span></div>}
              <div className="flex justify-between font-bold text-base border-t border-border pt-2 mt-2"><span>Da pagare in loco</span><span>€{total.toFixed(2)}</span></div>
            </div>
            <Button
              onClick={openBookDialog}
              disabled={busy || !date || (slotInfo && !slotInfo[slot].enabled)}
              className="w-full rounded-full bg-[#005C7A] hover:bg-[#004A63] text-white h-12 text-base"
              data-testid="book-button"
            >
              Prenota gratis · Paga in loco
            </Button>
            <p className="text-xs text-center text-muted-foreground mt-3">Nessun addebito online · confermi in un click</p>
          </div>
        </aside>
      </div>

      {/* Payment method dialog */}
      <Dialog open={payDialogOpen} onOpenChange={setPayDialogOpen}>
        <DialogContent data-testid="pay-method-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Come pagherai in struttura?</DialogTitle>
            <DialogDescription>
              La prenotazione è gratuita e immediata: l'importo di <strong className="text-[#005C7A]">€{total.toFixed(2)}</strong> verrà saldato direttamente all'hotel al momento dell'ingresso.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
            <button
              onClick={() => confirmBooking("cash")}
              disabled={busy}
              className="group text-left p-5 rounded-2xl border-2 border-border hover:border-[#005C7A] hover:bg-[#005C7A]/5 transition disabled:opacity-50"
              data-testid="pay-cash-btn"
            >
              <div className="w-11 h-11 rounded-xl bg-[#005C7A]/10 group-hover:bg-[#005C7A]/20 flex items-center justify-center mb-3 transition">
                <Wallet className="w-6 h-6 text-[#005C7A]" />
              </div>
              <div className="font-display text-lg font-semibold mb-0.5">Pagherò in contanti</div>
              <div className="text-xs text-muted-foreground">Salderai i €{total.toFixed(2)} in contanti alla reception all'arrivo.</div>
            </button>
            <button
              onClick={() => confirmBooking("card_on_site")}
              disabled={busy}
              className="group text-left p-5 rounded-2xl border-2 border-border hover:border-[#E27B58] hover:bg-[#E27B58]/5 transition disabled:opacity-50"
              data-testid="pay-card-btn"
            >
              <div className="w-11 h-11 rounded-xl bg-[#E27B58]/10 group-hover:bg-[#E27B58]/20 flex items-center justify-center mb-3 transition">
                <CreditCard className="w-6 h-6 text-[#E27B58]" />
              </div>
              <div className="font-display text-lg font-semibold mb-0.5">Pagherò con carta</div>
              <div className="text-xs text-muted-foreground">Userai il POS dell'hotel al tuo arrivo (€{total.toFixed(2)}).</div>
            </button>
          </div>
          <p className="text-[11px] text-muted-foreground pt-3 text-center">
            Riceverai subito email e QR code da mostrare in reception.
          </p>
        </DialogContent>
      </Dialog>

      {user?.role === "guest" && (
        <ChatDialog open={chatOpen} onOpenChange={setChatOpen} poolId={pool.pool_id} title={pool.hotel_name} />
      )}
    </div>
  );
}
